import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { InfoTooltip } from '@sales-center/components'
import Typography from '@mui/material/Typography'
import { selectAllEmployees } from '@user/store/employee/slice'
import { IEmployee } from '@user/store/employee/typings'
import { useSendMail } from '@user/store/mailCommunication/hooks/useSendMail'
import { ISendMail } from '@user/store/mailCommunication/typings'
import { selectAllUsers } from '@user/store/users/slice'
import { Field, useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { getStyles } from './styling'
import { IFormikUserModel, UpdateUserFormProps } from './typings'
import { useTheme } from '@mui/material/styles'
import { MuiSwitchFormik } from '@user/components/Formik'
import { useGetEmployeeOnMount } from '@user/store/employee/hooks/useGetEmployeeOnMount'
import useGetUserStatusToggleMsg from '@user/components/Settings/Users/hooks/useGetUserStatusToggleMsg'
import { inactiveUserUtilize } from '@user/components/Settings/Users/UpdateUser/constants'
import Box from '@mui/material/Box'
import { Select } from '@workwave-tidal/core/components'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { selectCurrentUser } from '@user/store/authorization'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import Stack from '@mui/material/Stack'
import EmployeeDetailsItem from './EmployeeDetailsItem'
import { userHasOpenRecords } from '../UsersNew/functions'

export const UpdateMappedUserFormNew: React.FC<UpdateUserFormProps> = (
  props,
) => {
  const {
    user,
    setReAssignDrawerOpen,
    initialActive,
    openReAssignDrawerAfterSave,
  } = props
  const employees = useSelector(selectAllEmployees)
  const tidalThemeActive = useTidalThemeActive()
  const currentUser = useSelector(selectCurrentUser)

  const hasOpenRecords = user ? userHasOpenRecords(user) : false

  useGetEmployeeOnMount(props.user?.externalUserId)
  const employeeWithWwId = useMemo(
    () =>
      employees
        ?.sort((a, b) => (b.firstName > a.firstName ? -1 : 1))
        .filter((e) => e.workWaveId != null && e.workWaveId.length > 0),
    [employees],
  )

  const allUsers = useSelector(selectAllUsers)
  const emailsAlreadyInUse = useMemo(
    () => allUsers.map((u) => u.emailAddress.toLowerCase()),
    [allUsers],
  )

  const {
    setFieldValue,
    setValues,
    errors,
    submitCount,
    values: { isActive },
  } = useFormikContext<IFormikUserModel>()
  const { status: sendMailStatus, execute: executeSendMail } = useSendMail()

  const getEmployeeFromUser = useCallback(
    (externalUserId: string): IEmployee | null => {
      const employee = employees.find((e) => e.id.toString() == externalUserId)
      return employee || null
    },
    [employees],
  )

  // ========== S T A T E ==========
  const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(
    props.user ? getEmployeeFromUser(props.user.externalUserId) : null,
  )
  const [welcomeEmailSent, setWelcomeEmailSent] = useState<boolean>(false)

  // ========== E V E N T S ==========
  const handleEmployeeSelectChange = useCallback(
    (value: IEmployee | null) => {
      setSelectedEmployee(value)
      setValues((v) => ({
        ...v,
        externalUser: value,
        lastName: value?.lastName ?? '',
        emailAddress: value?.workWaveId ?? '',
        firstName: value?.firstName ?? '',
      }))
    },
    [setValues],
  )

  // ========== H O O K S ==========

  const handleSendWelcomeEmailClick = useCallback(() => {
    if (selectedEmployee) {
      const sendModel: ISendMail = {
        type: 1,
        userEmail: selectedEmployee.workWaveId,
        id: props.user?.id,
        firstName: selectedEmployee.firstName,
        lastName: selectedEmployee.lastName,
      }
      executeSendMail(sendModel)
      setWelcomeEmailSent(true)
    }
  }, [executeSendMail, props.user?.id, selectedEmployee])

  const activeMessage = useGetUserStatusToggleMsg(user, user?.emailIsUnique)

  //#region autocomplete callbacks
  const getOptionDisabled = useCallback(
    (option: IEmployee) =>
      emailsAlreadyInUse.includes(option.workWaveId.toLowerCase()),
    [emailsAlreadyInUse],
  )

  const getOptionLabel = useCallback(
    (option: IEmployee) =>
      `${option.firstName} ${option.lastName} - ${option.workWaveId}`,
    [],
  )

  const onChangeStatus = useCallback(() => {
    if (user && userHasOpenRecords(user) && !openReAssignDrawerAfterSave) {
      setReAssignDrawerOpen(true)
    } else {
      setFieldValue('isActive', !isActive)
    }
  }, [
    activeMessage,
    openReAssignDrawerAfterSave,
    setReAssignDrawerOpen,
    setFieldValue,
    isActive,
    user,
  ])

  //#endregion

  // ========== H O O K S ==========
  useEffect(() => {
    if (user) {
      const employeeFromUser: IEmployee | null = getEmployeeFromUser(
        user.externalUserId,
      )
      if (employeeFromUser) {
        setValues((v) => ({
          ...v,
          externalUser: employeeFromUser,
          lastName: employeeFromUser?.lastName ?? '',
          emailAddress: employeeFromUser?.workWaveId ?? '',
          firstName: employeeFromUser?.firstName ?? '',
          emailUnique: Boolean(user?.emailIsUnique),
        }))
      }

      setFieldValue('isActive', user.isActive || initialActive)
    }
  }, [
    getEmployeeFromUser,
    initialActive,
    user?.externalUserId,
    setFieldValue,
    setValues,
  ])

  const styles = getStyles(useTheme())

  return (
    <>
      <Typography variant="h7">PestPac Employee Mapping</Typography>
      <Box mt={0.8}>
        <Select<IEmployee>
          disabled={!!props.user || !isActive}
          label="Employee"
          onChange={handleEmployeeSelectChange}
          value={selectedEmployee}
          items={employeeWithWwId}
          required
          optionMode="normal"
          getLabel={getOptionLabel}
          getOptionDisabled={getOptionDisabled}
          density={tidalThemeActive ? 'normal' : 'compact'}
          error={submitCount > 0 && Boolean(errors?.externalUser)}
        />
      </Box>
      {submitCount > 0 && Boolean(errors?.externalUser) ? (
        <FormHelperText error>{errors?.externalUser}</FormHelperText>
      ) : null}

      {/* ---------- M A P P E D   E M P L O Y E E   D E T A I L S ---------- */}
      {selectedEmployee && (
        <>
          {props.user ? (
            <>
              <Box mt={3.2} alignItems={'center'}>
                <Typography variant="h7">Status</Typography>
                <InfoTooltip
                  infoText={inactiveUserUtilize}
                  tooltipPlacement="right"
                  sx={{
                    ...styles.infoActiveIcon,
                    color: (theme) => theme.palette.action.active,
                    ml: 0.5,
                  }}
                />
              </Box>
              <Box ml={1} alignItems={'center'}>
                <Field
                  name="isActive"
                  component={MuiSwitchFormik}
                  disabled={
                    currentUser.userId === user?.id ||
                    user?.isSuperAdmin ||
                    (activeMessage !== undefined && !hasOpenRecords)
                  }
                  label={isActive ? 'Active' : 'Inactive'}
                  labelProps={{
                    sx: { marginTop: '10px' },
                  }}
                  onChange={onChangeStatus}
                />
                {activeMessage && !hasOpenRecords && (
                  <InfoTooltip
                    infoText={activeMessage}
                    tooltipPlacement="right"
                    sx={{
                      ...styles.infoActiveIcon,
                      color: (theme) => theme.palette.action.active,
                      mb: -0.2,
                      ml: -1,
                    }}
                  />
                )}
              </Box>
            </>
          ) : null}

          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            mt={props.user ? 1 : 3.2}
            mb={1.6}
          >
            <Typography component="div" variant="h7">
              Employee Details
            </Typography>
            <Button
              color="primary"
              variant="text"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 'medium',
              }}
              disabled={
                sendMailStatus == 'pending' ||
                selectedEmployee == null ||
                welcomeEmailSent ||
                !isActive
              }
              onClick={handleSendWelcomeEmailClick}
            >
              Send Welcome Email
            </Button>
          </Box>
          <Stack>
            <EmployeeDetailsItem
              title="Branch"
              value={selectedEmployee.defaultBranch}
            />
            <EmployeeDetailsItem
              title="First Name"
              value={selectedEmployee.firstName}
            />
            <EmployeeDetailsItem
              title="Last Name"
              value={selectedEmployee.lastName}
            />
            <EmployeeDetailsItem
              title="Email Address"
              value={selectedEmployee.workWaveId}
            />
            <EmployeeDetailsItem
              title="Time Zone"
              value={selectedEmployee.timeZone ?? ''}
            />
          </Stack>
        </>
      )}
    </>
  )
}
